"use client";

import React from "react";
import { useRouter, usePathname } from "next/navigation";
import { ContentCard } from "fansunited-sports-ui";
import { LinkPagination } from "@/components/ui/LinkPagination";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { AdSlot } from "@/features/ads/AdSlot";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { Post, Category } from "@/payload-types";
import { getMediaFieldUrl } from "@/utilities/media/getMediaUrl";
import type { LocaleCode } from "@/lib/labels/types";

interface VideosLayoutDefaultProps {
    posts: Post[];
    currentPage: number;
    totalPages: number;
    locale: LocaleCode;
    categories: Category[];
    selectedCategoryId: number | null;
    headerColors?: ResolvedHeaderColors;
    breadcrumbs?: React.ReactNode;
}

export function VideosLayoutDefault({
    posts,
    currentPage,
    totalPages,
    locale,
    categories,
    selectedCategoryId,
    headerColors,
    breadcrumbs,
}: VideosLayoutDefaultProps) {
    const labels = useLabels();
    const router = useRouter();
    const pathname = usePathname();

    const getPageHref = (page: number) => {
        // Preserve the category filter
        const params = new URLSearchParams();
        if (selectedCategoryId) {
            params.set("category", String(selectedCategoryId));
        }
        if (page > 1) {
            params.set("page", String(page));
        }
        return params.toString() ? `${pathname}?${params.toString()}` : pathname;
    };

    const handleCategoryChange = (categoryId: number | null) => {
        // Reset to page 1 when changing category
        const params = new URLSearchParams();
        if (categoryId) {
            params.set("category", String(categoryId));
        }
        const url = params.toString() ? `${pathname}?${params.toString()}` : pathname;
        router.push(url);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const getImageUrl = (heroImage: Post["heroImage"]): string | undefined => {
        return getMediaFieldUrl(heroImage, "medium") ?? undefined;
    };

    const headerTitle = (labels as any).nav?.videos ?? "Videos";

    return (
        <div className="min-h-screen">
            <BrandedHeader
                title={headerTitle}
                subtitle=""
                colors={headerColors?.colors}
                backgroundImageUrl={headerColors?.backgroundImageUrl}
            />

            {breadcrumbs}

            <div className="max-w-350 mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                {/* Category Filter */}
                {categories.length > 0 && (
                    <div className="mb-6 flex flex-wrap gap-2">
                        <button
                            onClick={() => handleCategoryChange(null)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                                selectedCategoryId === null
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted hover:bg-muted/80 text-foreground"
                            }`}
                        >
                            All
                        </button>
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => handleCategoryChange(category.id)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                                    selectedCategoryId === category.id
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted hover:bg-muted/80 text-foreground"
                                }`}
                            >
                                {category.title}
                            </button>
                        ))}
                    </div>
                )}

                {/* Posts Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map((post) => (
                        <ContentCard
                            key={post.id}
                            content={{
                                id: String(post.id),
                                type: "article",
                                link: `/videos/${post.slug}`,
                                title: post.title,
                                description: post.summary || "",
                                image: {
                                    url: getImageUrl(post.heroImage) || "",
                                    alt: post.title,
                                },
                                metadata: {
                                    time: formatDate(post.publishedAt),
                                },
                            }}
                            variant="standard"
                            styles={{
                                typography: {
                                    title: "text-lg font-semibold",
                                    description: "text-sm text-muted-foreground line-clamp-3",
                                },
                            }}
                        />
                    ))}
                </div>

                {/* Empty State */}
                {posts.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">No videos found.</div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                        <LinkPagination currentPage={currentPage} totalPages={totalPages} getPageHref={getPageHref} />
                    </div>
                )}

                <AdSlot id="content-bottom" />
            </div>
        </div>
    );
}
